import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject, Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import * as signalR from '@microsoft/signalr';
import { Injectable } from '@angular/core';

export interface StartChatSessionRequest {
  name: string;
  key: string;
  phone?: string;
  email?: string;
  language: string;
}

export interface StartChatSessionResponse {
  sessionId: number;
  success: boolean;
  message: string;
}

export interface SendMessageRequest {
  sessionId: number;
  message: string;
}

export interface SendMessageResponse {
  success: boolean;
  message: string;
}

export interface ChatMessageDto {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
  sessionId: number;
}

export interface AccountOpeningRequest {
  fullName: string;
  phoneNumber: string;
  email: string;
  address: string;
  hasExperience: boolean;
  tradesWithOtherCompany: boolean;
  language: string;
}

export interface AccountOpeningResponse {
  success: boolean;
  message: string;
  ticketId?: number;
}

export interface StockRecommendationRequest {
  accountNumber: string;
  language: string;
}

export interface NewClientTicketRequest {
  name: string;
  phone: string;
}

@Injectable({
  providedIn: 'root'
})
export class ChatService {
  private baseUrl = environment.apiUrl || 'https://www.aspiresecurities.com/webdataapi/api';
  private hubUrl = this.baseUrl.replace('/api', '/chatHub');
  private hubConnection: signalR.HubConnection | null = null;
  private messageReceivedSubject = new Subject<ChatMessageDto>();
  public messageReceived$ = this.messageReceivedSubject.asObservable();

  private currentSessionId: number | null = null;
  private messagesSubject = new BehaviorSubject<ChatMessageDto[]>([]);
  public messages$ = this.messagesSubject.asObservable();

  private httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };

  constructor(private http: HttpClient) { }

  async startChatSession(request: StartChatSessionRequest): Promise<StartChatSessionResponse> {
    try {
      const response = await this.http.post<StartChatSessionResponse>(
        `${this.baseUrl}/chat/start-session`,
        request,
        this.httpOptions
      ).toPromise();

      if (response && response.success) {
        this.currentSessionId = response.sessionId;
      }

      return response || { sessionId: 0, success: false, message: 'Unknown error' };
    } catch (error) {
      console.error('Error starting chat session:', error);
      return { sessionId: 0, success: false, message: 'Failed to start session' };
    }
  }

  async sendMessage(message: string): Promise<SendMessageResponse> {
    if (!this.currentSessionId) {
      return { success: false, message: 'No active session' };
    }

    try {
      const request: SendMessageRequest = {
        sessionId: this.currentSessionId,
        message: message
      };

      const response = await this.http.post<SendMessageResponse>(
        `${this.baseUrl}/chat/send-message`,
        request,
        this.httpOptions
      ).toPromise();

      return response || { success: false, message: 'Unknown error' };
    } catch (error) {
      console.error('Error sending message:', error);
      return { success: false, message: 'Failed to send message' };
    }
  }

  async endSession(): Promise<boolean> {
    if (!this.currentSessionId) {
      return false;
    }

    try {
      if (this.hubConnection) {
        await this.hubConnection.invoke('LeaveSession', this.currentSessionId);
        await this.hubConnection.stop();
        this.hubConnection = null;
      }

      const response = await this.http.post<boolean>(
        `${this.baseUrl}/chat/end-session/${this.currentSessionId}`,
        {},
        this.httpOptions
      ).toPromise();

      if (response) {
        this.currentSessionId = null;
        this.messagesSubject.next([]);
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error ending session:', error);
      return false;
    }
  }

  // SignalR Hub Methods
  async startHubConnection(): Promise<void> {
    if (this.hubConnection) return;

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(this.hubUrl, {
        skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets
      })
      .withAutomaticReconnect()
      .build();

    this.hubConnection.on('ReceiveMessage', (message: any) => {
      console.log('SignalR message received:', message);
      // Try both camelCase and PascalCase to be safe
      const text = message.textContent || message.TextContent || message.text;
      const id = message.id || message.Id;

      const chatMessage: ChatMessageDto = {
        id: id?.toString() || Date.now().toString(),
        text: text,
        isUser: false,
        timestamp: new Date(),
        sessionId: this.currentSessionId || 0
      };
      this.messageReceivedSubject.next(chatMessage);
    });

    try {
      await this.hubConnection.start();
      console.log('SignalR Hub connection started');

      if (this.currentSessionId) {
        await this.hubConnection.invoke('JoinSession', this.currentSessionId);
      }
    } catch (err) {
      console.error('Error while starting SignalR connection: ' + err);
    }
  }

  async joinSession(sessionId: number): Promise<void> {
    this.currentSessionId = sessionId;
    if (this.hubConnection && this.hubConnection.state === signalR.HubConnectionState.Connected) {
      await this.hubConnection.invoke('JoinSession', sessionId);
    } else {
      await this.startHubConnection();
    }
  }

  async getSessionMessages(): Promise<ChatMessageDto[]> {
    if (!this.currentSessionId) {
      return [];
    }

    try {
      const response = await this.http.get<ChatMessageDto[]>(
        `${this.baseUrl}/chat/messages/${this.currentSessionId}`,
        this.httpOptions
      ).toPromise();

      return response || [];
    } catch (error) {
      console.error('Error getting session messages:', error);
      return [];
    }
  }

  async getQueuePosition(key: string): Promise<number> {
    try {
      const response = await this.http.get<number>(
        `${this.baseUrl}/chat/queue-position/${key}`,
        this.httpOptions
      ).toPromise();

      return response || -1;
    } catch (error) {
      console.error('Error getting queue position:', error);
      return -1;
    }
  }

  getCurrentSessionId(): number | null {
    return this.currentSessionId;
  }

  addMessage(message: ChatMessageDto): void {
    const currentMessages = this.messagesSubject.value;
    this.messagesSubject.next([...currentMessages, message]);
  }

  clearMessages(): void {
    this.messagesSubject.next([]);
  }

  async createAccountOpeningTicket(request: AccountOpeningRequest): Promise<AccountOpeningResponse> {
    try {
      const response = await this.http.post<AccountOpeningResponse>(
        `${this.baseUrl}/chat/account-opening`,
        request,
        this.httpOptions
      ).toPromise();

      return response || { success: false, message: 'Unknown error' };
    } catch (error) {
      console.error('Error creating account opening ticket:', error);
      return { success: false, message: 'Failed to create account opening ticket' };
    }
  }

  async createStockRecommendationsTicket(request: StockRecommendationRequest): Promise<AccountOpeningResponse> {
    try {
      const response = await this.http.post<AccountOpeningResponse>(
        `${this.baseUrl}/chat/stock-recommendations`,
        request,
        this.httpOptions
      ).toPromise();

      return response || { success: false, message: 'Unknown error' };
    } catch (error) {
      console.error('Error creating stock recommendations ticket:', error);
      return { success: false, message: 'Failed to create stock recommendations ticket' };
    }
  }

  async createNewClientTicket(request: NewClientTicketRequest): Promise<boolean> {
    try {
      const response = await this.http.post<boolean>(
        `${this.baseUrl}/chat/new-client-ticket`,
        request,
        this.httpOptions
      ).toPromise();

      return response ?? false;
    } catch (error) {
      console.error('Error creating new client ticket:', error);
      return false;
    }
  }
} 